import React, { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

const PALETTE = {
  lightBg: "#FFFFFF",
  darkText: "#2B2B2B",
  tan: "#C7A583",
  tanSoft: "#EADCCB",
  lightHover: "#F8F9FA",
  line: "rgba(0,0,0,0.08)",
};

const ITEMS = ["Home", "About", "Services", "Portfolio", "Contact"];
const hrefFor = (label) => (label === "Home" ? "/" : `/${label.toLowerCase()}`);

const PORTFOLIO_ITEMS = [
  { label: "Interior", href: "./portfolio" },
  { label: "Management", href: "./management" }
];

/* Desktop link with sweep + underline (hover only) */
function DesktopNavLink({ label, href, hasDropdown = false, onMouseEnter, onMouseLeave }) {
  return (
    <a
      href={href}
      className="relative inline-block px-5 py-2.5 overflow-hidden"
      style={{ color: PALETTE.darkText }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span className="relative z-10 flex items-center gap-1">
        {label}
        {hasDropdown && <ChevronDown size={16} />}
      </span>

      {/* Background sweep on hover */}
      <span
        aria-hidden
        className="absolute inset-0 -z-10 -translate-x-full hover:translate-x-0 transition-transform duration-300 ease-out rounded-full"
        style={{ backgroundColor: PALETTE.lightHover }}
      />

      {/* Underline grow on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 bottom-0 h-[2px] w-0 transition-all duration-300"
        style={{ backgroundColor: PALETTE.tan }}
      />
      <style>{`
        a:hover > span.pointer-events-none { width: 100% !important; }
        a:hover { color: ${PALETTE.tan} !important; }
      `}</style>
    </a>
  );
}

/* Drawer item with full-width hover fill (no active) */
function DrawerItem({ label, href, onClick, hasDropdown = false, children }) {
  const [isOpen, setIsOpen] = useState(false);

  if (hasDropdown) {
    return (
      <div className="border-b" style={{ borderColor: PALETTE.line }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative block w-full px-5 py-4 overflow-hidden text-left"
          style={{ color: PALETTE.darkText, backgroundColor: "transparent" }}
        >
          <span className="relative z-10 flex items-center justify-between">
            {label}
            <ChevronDown 
              size={16} 
              className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </span>

          {/* Full-width fill with left-to-right wipe on hover */}
          <span
            aria-hidden
            className="absolute inset-0 -z-10 transform-gpu transition-transform duration-250 ease-out -translate-x-full hover:translate-x-0"
            style={{ backgroundColor: PALETTE.lightHover }}
          />

          {/* Text turns tan on hover */}
          <style>{`
            button:hover { color: ${PALETTE.tan} !important; }
          `}</style>
        </button>
        
        {isOpen && (
          <div className="bg-gray-50">
            {children}
          </div>
        )}
      </div>
    );
  }

  return (
    <a
      href={href}
      onClick={onClick}
      className="relative block px-5 py-4 overflow-hidden"
      style={{ color: PALETTE.darkText, backgroundColor: "transparent" }}
    >
      <span className="relative z-10">{label}</span>

      {/* Full-width fill with left-to-right wipe on hover */}
      <span
        aria-hidden
        className="absolute inset-0 -z-10 transform-gpu transition-transform duration-250 ease-out -translate-x-full hover:translate-x-0"
        style={{ backgroundColor: PALETTE.lightHover }}
      />

      {/* Text turns tan on hover */}
      <style>{`
        a:hover { color: ${PALETTE.tan} !important; }
      `}</style>
    </a>
  );
}

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [portfolioDropdownOpen, setPortfolioDropdownOpen] = useState(false);

  return (
    <>
      {/* Header */}
      <header
        className="fixed top-0 left-0 w-full z-50 border-b"
        style={{ backgroundColor: PALETTE.lightBg, borderColor: PALETTE.line }}
      >
        <div className="mx-auto max-w-7xl h-[65px] px-6 lg:px-12 flex items-center">
          {/* Logo */}
          <a href="/" aria-label="Go to homepage" className="inline-flex items-center gap-2">
            {/* <span className="inline-block h-6 w-6 rounded-sm" style={{ backgroundColor: PALETTE.tan }} /> */}
            <img src="./images/logo_name.png" alt="Manar logo" className="h-[2.3rem] w-auto select-none" style={{ filter: "invert(1) brightness(0.1) contrast(1.1)" }} />
          </a>

          <div className="flex-1" />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center tracking-wide">
            {ITEMS.map((item, idx) => (
              <div key={item} className="relative">
                {item === "Portfolio" ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setPortfolioDropdownOpen(true)}
                    onMouseLeave={() => setPortfolioDropdownOpen(false)}
                  >
                    <DesktopNavLink 
                      label={item} 
                      href="#" 
                      hasDropdown={true}
                    />
                    
                    {/* Desktop Dropdown */}
                    {portfolioDropdownOpen && (
                      <div 
                        className="absolute top-full left-0 mt-0 w-48 rounded-md shadow-lg z-50"
                        style={{ backgroundColor: PALETTE.lightBg, borderColor: PALETTE.line }}
                        onMouseEnter={() => setPortfolioDropdownOpen(true)}
                        onMouseLeave={() => setPortfolioDropdownOpen(false)}
                      >
                        <div className="py-1 border rounded-md" style={{ borderColor: PALETTE.line }}>
                          {PORTFOLIO_ITEMS.map((portfolioItem) => (
                            <a
                              key={portfolioItem.label}
                              href={portfolioItem.href}
                              className="block px-4 py-2 hover:bg-opacity-50 transition-colors duration-200"
                              style={{ 
                                color: PALETTE.darkText,
                                backgroundColor: "transparent"
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = PALETTE.lightHover;
                                e.target.style.color = PALETTE.tan;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "transparent";
                                e.target.style.color = PALETTE.darkText;
                              }}
                            >
                              {portfolioItem.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <DesktopNavLink label={item} href={hrefFor(item)} />
                )}
                {idx !== ITEMS.length - 1 && (
                  <span
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-px"
                    style={{ backgroundColor: PALETTE.line }}
                  />
                )}
              </div>
            ))}
          </nav>

          {/* Hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="md:hidden ml-3 p-2 rounded-md"
            aria-label="Open menu"
            style={{ color: PALETTE.darkText }}
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      {/* Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Light drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[380px] z-50 bg-white shadow-xl transform transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer header */}
        <div
          className="flex items-center justify-between h-16 px-5 border-b"
          style={{ borderColor: PALETTE.line }}
        >
          <div className="inline-flex items-center gap-2">
            <span className="inline-block h-5 w-5 rounded-sm" style={{ backgroundColor: PALETTE.tan }} />
            <span className="font-semibold" style={{ color: PALETTE.darkText }}>
              MANAR
            </span>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-2"
            aria-label="Close menu"
            style={{ color: PALETTE.darkText }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Drawer nav with full-width hover */}
        <nav className="flex flex-col">
          {ITEMS.map((item, idx) => (
            <div key={item}>
              {item === "Portfolio" ? (
                <DrawerItem
                  label={item}
                  hasDropdown={true}
                >
                  {PORTFOLIO_ITEMS.map((portfolioItem) => (
                    <a
                      key={portfolioItem.label}
                      href={portfolioItem.href}
                      onClick={() => setDrawerOpen(false)}
                      className="block px-8 py-3 text-sm hover:bg-opacity-50 transition-colors duration-200"
                      style={{ 
                        color: PALETTE.darkText,
                        backgroundColor: "transparent"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = PALETTE.lightHover;
                        e.target.style.color = PALETTE.tan;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color = PALETTE.darkText;
                      }}
                    >
                      {portfolioItem.label}
                    </a>
                  ))}
                </DrawerItem>
              ) : (
                <div className="border-b" style={{ borderColor: PALETTE.line }}>
                  <DrawerItem
                    label={item}
                    href={hrefFor(item)}
                    onClick={() => setDrawerOpen(false)}
                  />
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
